<template>
  <div
    ref="containerRef"
    class="pdf-player"
    tabindex="0"
    :class="{
      'pdf-player--fullscreen': player.isFullscreen.value,
      'pdf-player--loading': player.isDocumentLoading.value,
      'pdf-player--light': theme === 'light',
    }"
    :style="primaryColorStyle"
  >
    <!-- Toolbar -->
    <PDFToolbar
      :current-page="player.currentPage.value"
      :total-pages="player.totalPages.value"
      :zoom="player.zoom.value"
      :zoom-mode="player.zoomMode.value"
      :zoom-presets="player.zoomPresets"
      :view-mode="player.viewMode.value"
      :is-fullscreen="player.isFullscreen.value"
      :show-thumbnails="showThumbnails"
      :show-search="showSearch"
      :enable-search="config.enableSearch !== false"
      :enable-print="config.enablePrint !== false"
      :enable-download="config.enableDownload !== false"
      :enable-rotation="config.enableRotation !== false"
      @prev-page="handlePrevPage"
      @next-page="handleNextPage"
      @go-to-page="handleGoToPage"
      @zoom-in="player.zoomIn"
      @zoom-out="player.zoomOut"
      @set-zoom="player.setZoom"
      @set-view-mode="player.setViewMode"
      @toggle-fullscreen="handleToggleFullscreen"
      @rotate="player.rotate"
      @print="handlePrint"
      @download="handleDownload"
      @update:show-thumbnails="showThumbnails = $event"
      @update:show-search="handleToggleSearch"
    />

    <div class="pdf-player__body">
      <Transition name="pdf-search-panel">
        <PDFSearchBar
          v-if="showSearch && config.enableSearch !== false"
          :query="player.searchQuery.value"
          :suggestions="player.searchSuggestions.value"
          :navigable-count="player.navigableSearchResults.value.length"
          :current-index="player.currentSearchIndex.value"
          :is-searching="player.isSearching.value"
          :search-mode="player.searchMode.value"
          :minimized="searchMinimized"
          @search="player.search"
          @clear="handleSearchClear"
          @close="handleSearchClose"
          @minimize="searchMinimized = true"
          @expand="searchMinimized = false"
          @prev-result="player.prevSearchResult"
          @next-result="player.nextSearchResult"
          @select-view-all="player.selectViewAll"
          @select-word="player.selectWord"
        />
      </Transition>

      <!-- Main content area -->
      <div class="pdf-player__content">

      <!-- Mobile: the sidebars in a drawer over the document. **Not `v-navigation-drawer`**: that
           one is a layout item and needs a `v-layout` around it, which a player mounted inside
           somebody else's page has not — Vuetify threw « Could not find injected layout » the
           moment a PDF opened on a phone (QA review 2026-10-16). -->
      <template v-if="mobile">
        <Transition name="pdf-drawer-scrim">
          <div v-if="mobileSidebarOpen" class="pdf-player__drawer-scrim" @click="mobileSidebarOpen = false" />
        </Transition>
        <Transition name="pdf-sidebar-slide">
          <aside v-if="mobileSidebarOpen" class="pdf-player__drawer">
            <PDFThumbnailSidebar
              v-if="showThumbnails"
              :document="player.document.value"
              :total-pages="player.totalPages.value"
              :current-page="player.currentPage.value"
              :rotation="player.rotation.value"
              :thumbnail-cache="thumbnailCache"
              @select="handleMobileSidebarSelect"
            />
            <PDFOutlineSidebar
              v-if="showOutline && player.outline.value.length > 0"
              :outline="player.outline.value"
              :current-page="player.currentPage.value"
              @select="handleMobileSidebarSelect"
            />
            <PDFCollectionSidebar
              v-if="showCollection && hasCollection"
              :items="config.items"
              :current-index="config.currentIndex"
              @select="handleCollectionSelect"
            />
          </aside>
        </Transition>
      </template>

      <!-- Desktop: inline sidebars -->
      <template v-else>
        <Transition name="pdf-sidebar-slide">
          <PDFThumbnailSidebar
            v-if="showThumbnails"
            :document="player.document.value"
            :total-pages="player.totalPages.value"
            :current-page="player.currentPage.value"
            :rotation="player.rotation.value"
            :thumbnail-cache="thumbnailCache"
            @select="handleGoToPage"
          />
        </Transition>

        <Transition name="pdf-sidebar-slide">
          <PDFOutlineSidebar
            v-if="showOutline && player.outline.value.length > 0"
            :outline="player.outline.value"
            :current-page="player.currentPage.value"
            @select="handleGoToPage"
          />
        </Transition>

        <Transition name="pdf-sidebar-slide">
          <PDFCollectionSidebar
            v-if="showCollection && hasCollection"
            :items="config.items"
            :current-index="config.currentIndex"
            @select="handleCollectionSelect"
          />
        </Transition>
      </template>

      <!-- Viewer area -->
      <div
        ref="viewerRef"
        class="pdf-player__viewer"
        @scroll="handleScroll"
      >
        <!-- Loading state -->
        <div v-if="player.isDocumentLoading.value" class="pdf-player__loading">
          <v-progress-circular
            indeterminate
            size="64"
            width="4"
            color="primary"
          />
          <span class="pdf-player__loading-text">{{ t('viewer.loadingDocument') }}</span>
        </div>

        <!-- Error state -->
        <div v-else-if="player.documentError.value" class="pdf-player__error">
          <v-icon color="error" size="64" :icon="mdiAlertCircle" />
          <span class="pdf-player__error-text">
            {{ player.documentError.value.message }}
          </span>
          <v-btn
            variant="tonal"
            color="primary"
            class="mt-4"
            @click="loadDocument"
          >
            {{ t('viewer.retry') }}
          </v-btn>
        </div>

        <!-- Continuous mode -->
        <div
          v-if="player.viewMode.value === 'continuous'"
          class="pdf-player__continuous-view"
        >
          <div
            v-for="pageNum in player.totalPages.value"
            :key="pageNum"
            :ref="(el) => setPageRef(pageNum, el as HTMLElement | null)"
            class="pdf-player__page-wrapper"
            :data-page="pageNum"
          >
            <PDFCanvas
              v-if="isPageVisible(pageNum)"
              :page="pageProxies.get(pageNum) || null"
              :zoom="player.zoom.value"
              :rotation="player.rotation.value"
              :enable-text-layer="config.enableTextSelection !== false"
              :render-cache="renderCache"
              :search-highlights="highlightsForPage(pageNum)"
              :active-highlight-index="player.activeHighlightMatchIndex.value"
              @rendered="handlePageRendered"
            />
            <div
              v-else
              class="pdf-player__page-placeholder"
              :style="placeholderStyle"
            />
          </div>
        </div>

        <!-- Two-page mode (scrollable pairs) -->
        <div
          v-else-if="player.viewMode.value === 'two-page'"
          class="pdf-player__two-page-continuous-view"
        >
          <div
            v-for="pair in twoPagePairs"
            :key="pair[0]"
            class="pdf-player__two-page-row"
          >
            <div
              v-for="pageNum in pair"
              :key="pageNum"
              :ref="(el) => setPageRef(pageNum, el as HTMLElement | null)"
              class="pdf-player__page-wrapper"
              :data-page="pageNum"
            >
              <PDFCanvas
                v-if="isPageVisible(pageNum)"
                :page="pageProxies.get(pageNum) || null"
                :zoom="player.zoom.value"
                :rotation="player.rotation.value"
                :enable-text-layer="config.enableTextSelection !== false"
                :render-cache="renderCache"
                :search-highlights="highlightsForPage(pageNum)"
                :active-highlight-index="player.activeHighlightMatchIndex.value"
                @rendered="handlePageRendered"
              />
              <div
                v-else
                class="pdf-player__page-placeholder"
                :style="placeholderStyle"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- Logo overlay — bottom-right -->
    <component
      :is="logoClickUrl ? 'a' : 'div'"
      v-if="logoUrl"
      class="pdf-player__logo"
      :href="logoClickUrl || undefined"
      :target="logoClickUrl ? '_blank' : undefined"
      :rel="logoClickUrl ? 'noopener noreferrer' : undefined"
    >
      <img :src="logoUrl" alt="" class="pdf-player__logo-img" />
    </component>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  shallowRef,
  markRaw,
  nextTick,
  type PropType,
  type Ref,
} from 'vue'
import { mdiAlertCircle } from '@mdi/js'
import type { PDFPageProxy } from 'pdfjs-dist'
import { useFullscreen, useResizeObserver } from '@vueuse/core'
import { useDisplay } from 'vuetify'
import type { PDFPlayerConfig, PDFOutlineItem } from '../types'
import type { DocumentPlaybackInfo } from '../../types/asset'
import type { PlayerError } from '../../types/base'
import { usePDFPlayer } from '../composables/usePDFPlayer'
import { usePinchZoom } from '../composables/usePinchZoom'
import { useReadToEnd } from '../composables/useReadToEnd'
import { hexToRgb } from '../../audio/utils/audio-utils'
import PDFToolbar from './PDFToolbar.vue'
import PDFSearchBar from './PDFSearchBar.vue'
import PDFCanvas from './PDFCanvas.vue'
import PDFThumbnailSidebar from './PDFThumbnailSidebar.vue'
import PDFOutlineSidebar from './PDFOutlineSidebar.vue'
import PDFCollectionSidebar from './PDFCollectionSidebar.vue'
import { usePDFI18n } from '../i18n'

const { t } = usePDFI18n()

const props = defineProps({
  config: {
    type: Object as PropType<PDFPlayerConfig>,
    required: true,
  },
  playbackInfo: {
    type: Object as PropType<DocumentPlaybackInfo>,
    default: undefined,
  },
  poster: {
    type: String,
    default: undefined,
  },
  /** Player theme */
  theme: {
    type: String as PropType<'dark' | 'light'>,
    default: 'dark',
  },
  /** Primary color override (CSS color value, e.g. hex) */
  primaryColor: {
    type: String,
    default: undefined,
  },
  /** Workspace logo URL to overlay on the player */
  logoUrl: {
    type: String,
    default: undefined,
  },
  /** Optional URL the logo links to when clicked */
  logoClickUrl: {
    type: String,
    default: undefined,
  },
})

const primaryColorStyle = computed<Record<string, string> | undefined>(() => {
  if (!props.primaryColor) return undefined
  const rgb = hexToRgb(props.primaryColor)
  if (!rgb) return undefined
  return { '--v-theme-primary': `${rgb[0]}, ${rgb[1]}, ${rgb[2]}` }
})

const emit = defineEmits<{
  documentLoad: [totalPages: number, outline?: PDFOutlineItem[]]
  error: [error: PlayerError]
  pageChange: [page: number]
  zoomChange: [zoom: number, mode: string | null]
  viewModeChange: [mode: string]
  searchStart: [query: string]
  searchResults: [results: { pageNumber: number; text: string }[]]
  itemChange: [index: number]
  print: []
  download: []
  /**
   * The reader reached the document's last page — paged or scrolled to it; a one-page document
   * once it is displayed — once per document opened (`../composables/useReadToEnd.ts`).
   */
  ended: []
}>()

// Refs - explicitly typed for @vueuse/core compatibility
const containerRef: Ref<HTMLElement | null> = ref(null)
const viewerRef: Ref<HTMLElement | null> = ref(null)
const pageRefs = new Map<number, HTMLElement>()

// UI state
const showThumbnails = ref(props.config.showThumbnails ?? false)
const showOutline = ref(props.config.showOutline ?? false)
const showSearch = ref(false)
const searchMinimized = ref(false)
const showCollection = ref(props.config.showCollection ?? false)

// Open mobile drawer when any sidebar is toggled on
watch([showThumbnails, showOutline, showCollection], ([thumbs, outline, collection]) => {
  if (mobile.value && (thumbs || outline || collection)) {
    mobileSidebarOpen.value = true
  }
})
const hasCollection = computed(() => props.config.items.length > 1)

// Page proxies for continuous mode
const pageProxies = shallowRef(new Map<number, PDFPageProxy>())
const visiblePages = ref(new Set<number>())
// Page pairs for two-page scrollable mode
const twoPagePairs = computed(() => {
  const pairs: number[][] = []
  for (let i = 1; i <= player.totalPages.value; i += 2) {
    const pair = [i]
    if (i + 1 <= player.totalPages.value) {
      pair.push(i + 1)
    }
    pairs.push(pair)
  }
  return pairs
})

// Render cache — stores ImageBitmap of rendered pages to avoid re-rendering via pdf.js
const renderCache = new Map<string, ImageBitmap>()
const thumbnailCache = new Map<number, ImageBitmap>()

// Placeholder dimensions — updated from actual rendered page size to avoid scroll jumps
const renderedPageWidth = ref(0)
const renderedPageHeight = ref(0)
const placeholderStyle = computed(() => {
  if (renderedPageWidth.value && renderedPageHeight.value) {
    return {
      width: `${renderedPageWidth.value}px`,
      height: `${renderedPageHeight.value}px`,
    }
  }
  return { width: '612px', height: '792px' }
})

// Search highlights — filter by page for each canvas instance
function highlightsForPage(pageNumber: number) {
  return player.searchResults.value.filter((r) => r.pageNumber === pageNumber)
}

// Fullscreen
const { isFullscreen, toggle: toggleFullscreen } = useFullscreen(containerRef)

// Mobile detection
const { mobile } = useDisplay()
const mobileSidebarOpen = ref(false)

// A document has no playback to finish, so it ends once its last page is reached. The rule reads
// the player declared below, and that player's callbacks call it, once both exist.
const readToEnd = useReadToEnd({
  totalPages: () => player.totalPages.value,
  currentPage: () => player.currentPage.value,
  onEnded: () => emit('ended'),
})

// Initialize player
const player = usePDFPlayer({
  config: props.config,
  callbacks: {
    onDocumentLoad: (totalPages, outline) => {
      emit('documentLoad', totalPages, outline)
      initializeVisiblePages()
    },
    onError: (error) => {
      emit('error', error)
    },
    onPageChange: (page) => {
      emit('pageChange', page)
      readToEnd.check(viewerRef.value)
    },
    onZoomChange: (zoom, mode) => {
      emit('zoomChange', zoom, mode)
    },
    onViewModeChange: (mode) => {
      emit('viewModeChange', mode)
    },
    onSearchStart: (query) => {
      emit('searchStart', query)
    },
    onSearchResults: (results) => {
      emit('searchResults', results)
    },
    onSearchResultSelect: (result) => {
      const pageNum = result.pageNumber
      if (!visiblePages.value.has(pageNum)) {
        const newVisible = new Set(visiblePages.value)
        newVisible.add(pageNum)
        visiblePages.value = newVisible
        loadVisiblePages()
      }
      scrollToPage(pageNum)
    },
    onPrint: () => {
      emit('print')
    },
    onDownload: () => {
      emit('download')
    },
  },
})

// Pinch-to-zoom on touch devices
usePinchZoom({
  containerRef: viewerRef,
  currentZoom: player.zoom,
  onZoomChange: (zoom) => player.setZoom(zoom),
})

// Page reference management
function setPageRef(pageNum: number, el: HTMLElement | null): void {
  if (el) {
    pageRefs.set(pageNum, el)
  } else {
    pageRefs.delete(pageNum)
  }
}

// Visible page calculation
function isPageVisible(pageNum: number): boolean {
  return visiblePages.value.has(pageNum)
}

function initializeVisiblePages(): void {
  const baseRenderAhead = props.config.renderAhead ?? 2
  const renderAhead = player.viewMode.value === 'two-page' ? baseRenderAhead * 2 : baseRenderAhead
  const current = player.currentPage.value
  const newVisible = new Set<number>()

  for (let i = Math.max(1, current - renderAhead); i <= Math.min(player.totalPages.value, current + renderAhead); i++) {
    newVisible.add(i)
  }

  visiblePages.value = newVisible
  loadVisiblePages()
}

async function loadVisiblePages(): Promise<void> {
  const newProxies = new Map(pageProxies.value)

  for (const pageNum of visiblePages.value) {
    if (!newProxies.has(pageNum)) {
      try {
        const page = await player.getPage(pageNum)
        // Use markRaw to prevent Vue reactivity on pdf.js objects
        newProxies.set(pageNum, markRaw(page))
      } catch (error) {
        console.error(`Failed to load page ${pageNum}:`, error)
      }
    }
  }

  pageProxies.value = newProxies
}

// True while a programmatic scroll animation is in progress — suppresses handleScroll
let isNavScrolling = false

// Scrolls to a page element in continuous mode (within the viewer only)
function scrollToPage(pageNum: number): void {
  const el = pageRefs.get(pageNum)
  if (!el || !viewerRef.value) return

  isNavScrolling = true

  const viewerTop = viewerRef.value.getBoundingClientRect().top
  const elTop = el.getBoundingClientRect().top
  viewerRef.value.scrollTop += elTop - viewerTop
  isNavScrolling = false
}

// Navigation wrappers that scroll in continuous mode
function handleMobileSidebarSelect(page: number): void {
  mobileSidebarOpen.value = false
  handleGoToPage(page)
}

function handleGoToPage(page: number): void {
  player.goToPage(page)
  initializeVisiblePages()
  scrollToPage(page)
}

function handlePrevPage(): void {
  player.prevPage()
  initializeVisiblePages()
  scrollToPage(player.currentPage.value)
}

function handleNextPage(): void {
  player.nextPage()
  initializeVisiblePages()
  scrollToPage(player.currentPage.value)
}

// Scroll handler for continuous mode
function handleScroll(): void {
  if (!viewerRef.value || isNavScrolling) return
  readToEnd.check(viewerRef.value)

  const viewerRect = viewerRef.value.getBoundingClientRect()
  const viewerCenter = viewerRect.top + viewerRect.height / 2
  let closestPage = player.currentPage.value
  let closestDistance = Infinity

  for (const [pageNum, el] of pageRefs) {
    const rect = el.getBoundingClientRect()
    const pageCenter = rect.top + rect.height / 2
    const distance = Math.abs(pageCenter - viewerCenter)

    if (distance < closestDistance) {
      closestDistance = distance
      closestPage = pageNum
    }
  }

  if (closestPage !== player.currentPage.value) {
    player.goToPage(closestPage)
  }

  // Update visible pages
  const renderAhead = props.config.renderAhead ?? 2
  const newVisible = new Set<number>()

  for (let i = Math.max(1, closestPage - renderAhead); i <= Math.min(player.totalPages.value, closestPage + renderAhead); i++) {
    newVisible.add(i)
  }

  if (!setsEqual(visiblePages.value, newVisible)) {
    visiblePages.value = newVisible
    loadVisiblePages()
  }
}

function setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) return false
  for (const item of a) {
    if (!b.has(item)) return false
  }
  return true
}

// Event handlers
function handlePageRendered(dimensions: { width: number; height: number }): void {
  renderedPageWidth.value = Math.floor(dimensions.width)
  renderedPageHeight.value = Math.floor(dimensions.height)
  // The page drawn resizes the placeholders of the pages not drawn yet, and Vue writes that to
  // the DOM at its next update. Measured before it, the viewer would still scroll over their
  // default size: a document that fits once they shrink would never be at its end, with no scroll
  // left to make and no page to turn.
  void nextTick(() => readToEnd.displayed(viewerRef.value))
}

function handleRenderError(error: Error): void {
  emit('error', {
    code: 'PLAYBACK_ERROR',
    message: error.message,
    originalError: error,
    recoverable: true,
  })
}

function handleToggleSearch(): void {
  if (showSearch.value) {
    // If expanded → minimize (keeps search state). If already minimized → hide.
    if (searchMinimized.value) {
      showSearch.value = false
      searchMinimized.value = false
    } else {
      searchMinimized.value = true
    }
  } else {
    // Open expanded
    showSearch.value = true
    searchMinimized.value = false
  }
}

function handleSearchClear(): void {
  player.clearSearch()
}

function handleSearchClose(): void {
  player.clearSearch()
  showSearch.value = false
  searchMinimized.value = false
}

function handleToggleFullscreen(): void {
  toggleFullscreen()
}

function handlePrint(): void {
  player.print()
}

function handleDownload(): void {
  player.download()
}

function handleCollectionSelect(index: number): void {
  if (index === props.config.currentIndex) return

  // Clean up caches before switching documents
  for (const bitmap of renderCache.values()) {
    bitmap.close()
  }
  renderCache.clear()
  for (const bitmap of thumbnailCache.values()) {
    bitmap.close()
  }
  thumbnailCache.clear()

  // Reset search
  player.clearSearch()
  showSearch.value = false
  searchMinimized.value = false

  emit('itemChange', index)
}

async function loadDocument(): Promise<void> {
  if (props.playbackInfo?.url?.url) {
    readToEnd.opened()
    await player.loadDocument(props.playbackInfo.url.url)
  }
}

// Resize observer for zoom mode recalculation
useResizeObserver(viewerRef, (entries) => {
  const entry = entries[0]
  if (entry) {
    player.updateContainerSize({
      width: entry.contentRect.width,
      height: entry.contentRect.height,
    })
  }
  // A viewer grown to show the rest of the document — fullscreen, a larger window — reaches its
  // end without a scroll.
  readToEnd.check(viewerRef.value)
})

// Clear render cache when zoom or rotation changes (cached bitmaps are zoom/rotation-specific)
watch(
  () => [player.zoom.value, player.rotation.value],
  () => {
    for (const bitmap of renderCache.values()) {
      bitmap.close()
    }
    renderCache.clear()
  },
)

// Watch for view mode changes — initialize visible pages
watch(
  () => player.viewMode.value,
  () => {
    initializeVisiblePages()
  },
)

// Watch for playback info changes (collection item switch)
watch(
  () => props.playbackInfo,
  (newInfo, oldInfo) => {
    if (newInfo && newInfo !== oldInfo) {
      // Reset page state
      player.goToPage(1)
      visiblePages.value = new Set<number>()
      pageProxies.value = new Map()

      loadDocument()
    }
  },
)

// Watch fullscreen state
watch(isFullscreen, (value) => {
  if (value !== player.isFullscreen.value) {
    player.toggleFullscreen()
  }
})

// Initialize
onMounted(() => {
  // Seed container size synchronously from the actual viewer rect before the
  // first zoom calculation, otherwise setZoom('auto') uses the stale default
  // (800x600) until the ResizeObserver fires — the user sees a small viewer
  // that grows on first resize.
  const rect = viewerRef.value?.getBoundingClientRect()
  if (rect && rect.width > 0 && rect.height > 0) {
    player.updateContainerSize({ width: rect.width, height: rect.height })
  }
  loadDocument()
})

// Cleanup
onUnmounted(() => {
  for (const bitmap of renderCache.values()) {
    bitmap.close()
  }
  renderCache.clear()
  for (const bitmap of thumbnailCache.values()) {
    bitmap.close()
  }
  thumbnailCache.clear()
  player.destroy()
})
</script>

<style lang="scss" scoped>
@use '@/styles/design-tokens' as *;

.pdf-player {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: rgb(var(--v-theme-surface-variant, 82, 86, 89));
  border-radius: $radius-lg;
  outline: none;
  overflow: hidden;

  @include focus-visible-only;

  &--fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    height: 100dvh;
    z-index: 9999;
    border-radius: 0;
  }

  &__body {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  &__content {
    display: flex;
    flex: 1;
    overflow: hidden;
  }

  // The phone's drawer: over the document, from the left, behind a scrim that closes it.
  &__drawer-scrim {
    position: absolute;
    inset: 0;
    z-index: 2;
    background: rgba(0, 0, 0, 0.45);
  }

  &__drawer {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 3;
    display: flex;
    flex-direction: column;
    width: min(280px, 85%);
    overflow-y: auto;
    background: rgb(var(--v-theme-surface));
  }

  &__viewer {
    flex: 1;
    overflow: auto;
    padding: $space-xl;
    display: flex;
    flex-direction: column;
    align-items: center;
    -webkit-overflow-scrolling: touch;

    @media (max-width: 600px) {
      padding: $space-sm;
    }
  }

  &__loading,
  &__error {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: $space-lg;
    color: rgb(var(--v-theme-on-surface));
  }

  &__loading-text,
  &__error-text {
    font-size: $text-md;
    color: rgba(var(--v-theme-on-surface), 0.8);
    text-align: center;
    max-width: 300px;
  }

  &__continuous-view {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-xl;
  }

  &__two-page-continuous-view {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-xl;
  }

  &__two-page-row {
    display: flex;
    justify-content: center;
    gap: $space-xl;
  }

  &__page-wrapper {
    display: flex;
    justify-content: center;
  }

  &__page-placeholder {
    background: rgba(var(--v-theme-on-surface), 0.1);
    border-radius: $radius-sm;
  }

  &__logo {
    position: absolute;
    right: 12px;
    bottom: 12px;
    z-index: 10;
    pointer-events: auto;
    line-height: 0;
    transition: opacity 0.3s ease;

    &:focus-visible {
      outline: 2px solid rgba(255, 255, 255, 0.8);
      outline-offset: 2px;
      border-radius: 4px;
    }
  }

  &__logo-img {
    max-height: 40px;
    max-width: 120px;
    object-fit: contain;
    display: block;
  }
}

// The phone drawer's scrim
.pdf-drawer-scrim-enter-active,
.pdf-drawer-scrim-leave-active {
  transition: opacity $duration-normal $ease-default;
}

.pdf-drawer-scrim-enter-from,
.pdf-drawer-scrim-leave-to {
  opacity: 0;
}

// Sidebar slide-in transition
.pdf-sidebar-slide-enter-active,
.pdf-sidebar-slide-leave-active {
  transition: transform $duration-normal $ease-default, opacity $duration-normal $ease-default;
}

.pdf-sidebar-slide-enter-from,
.pdf-sidebar-slide-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

@include reduced-motion {
  .pdf-sidebar-slide-enter-active,
  .pdf-sidebar-slide-leave-active {
    transition: none;
  }
}

// Search panel transition
.pdf-search-panel-enter-active,
.pdf-search-panel-leave-active {
  transition: opacity $duration-normal $ease-default, transform $duration-normal $ease-default;
}

.pdf-search-panel-enter-from,
.pdf-search-panel-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@include reduced-motion {
  .pdf-search-panel-enter-active,
  .pdf-search-panel-leave-active {
    transition: none;
  }
}
</style>
